import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MessageCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brl = (v) => (v === null || v === undefined || v === '')
  ? '—'
  : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const day = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};
const SIGN = 'NOVO DNA Instituto de Aprendizagem';

const buildTemplates = (t, link, linha) => {
  const resp = (t.responsible_name || 'responsável').split(' ')[0];
  const aluno = t.student_name || '—';
  const desc = t.description || t.category || 'mensalidade';
  const parcelaSuffix = t.installment ? ` — ${t.installment}` : '';
  const venc = day(t.due_date);
  const saldo = brl(t.balance ?? t.amount_display);
  const linkLine = link ? `${link}` : 'Boleto: solicite à secretaria (PDF indisponível)';
  const linhaLine = linha || 'não disponível';

  return [
    {
      key: 'amigavel',
      label: 'Amigável',
      text:
`Olá, ${resp}! Tudo bem? 😊

Identificamos em nosso sistema o título de ${desc}, referente ao(à) aluno(a) ${aluno}, com vencimento em ${venc} e saldo de ${saldo}.

Segue o boleto para pagamento:
${linkLine}

Linha digitável:
${linhaLine}

Caso o pagamento já tenha sido realizado, por favor desconsidere esta mensagem.

${SIGN}`,
    },
    {
      key: 'objetiva',
      label: 'Objetiva',
      text:
`Olá, ${resp}.

Segue cobrança referente a ${desc}${parcelaSuffix}, do(a) aluno(a) ${aluno}.

Vencimento: ${venc}
Valor em aberto: ${saldo}

Boleto:
${linkLine}

Linha digitável:
${linhaLine}

${SIGN}`,
    },
    {
      key: 'vencido',
      label: 'Vencido',
      text:
`Olá, ${resp}.

Consta em nosso sistema um título vencido referente a ${desc}${parcelaSuffix}, do(a) aluno(a) ${aluno}.

Vencimento: ${venc}
Saldo atualizado: ${saldo}

Boleto:
${linkLine}

Linha digitável:
${linhaLine}

Caso já tenha efetuado o pagamento, desconsidere esta mensagem. Permanecemos à disposição.

${SIGN}`,
    },
  ];
};

export const WhatsappSendDialog = ({ titleId, open, onClose }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [tpl, setTpl] = useState('amigavel');
  const [text, setText] = useState('');

  useEffect(() => {
    if (!open || !titleId) return;
    setLoading(true);
    setCtx(null);
    api.get(`/financial/titles/${titleId}/whatsapp-context`)
      .then(r => {
        setCtx(r.data);
        setPhone(r.data.phones?.[0]?.value || '');
      })
      .catch(e => toast.error(`Não foi possível executar esta ação: ${e?.response?.data?.detail || 'falha ao carregar dados do título'}`))
      .finally(() => setLoading(false));
  }, [open, titleId]);

  const templates = useMemo(() => {
    if (!ctx) return [];
    return buildTemplates(ctx.title, ctx.boleto_link?.url, ctx.linha_digitavel);
  }, [ctx]);

  useEffect(() => {
    if (!templates.length) return;
    const chosen = templates.find(x => x.key === tpl) || templates[0];
    setText(chosen.text);
  }, [templates, tpl]);

  useEffect(() => {
    if (ctx?.title?.status === 'vencido') setTpl('vencido');
  }, [ctx]);

  const openWhatsapp = () => {
    if (!phone) { toast.error('Responsável sem telefone cadastrado. Cadastre um número para enviar pelo WhatsApp.'); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" /> Enviar pelo WhatsApp
          </DialogTitle>
          <DialogDescription>
            Envio manual: escolha o texto, ajuste se quiser e abra a conversa no seu WhatsApp.
          </DialogDescription>
        </DialogHeader>

        {loading || !ctx ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Telefone do responsável</Label>
              {ctx.phones?.length ? (
                <Select value={phone} onValueChange={setPhone}>
                  <SelectTrigger className="mt-1" data-testid="whatsapp-phone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ctx.phones.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-1 flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800"
                   data-testid="whatsapp-no-phone">
                  <AlertTriangle className="w-3.5 h-3.5" /> Responsável sem telefone cadastrado. Cadastre um número no perfil do responsável.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">Modelo da mensagem</Label>
              <div className="mt-1 flex gap-2">
                {templates.map(t => (
                  <button key={t.key} type="button" onClick={() => setTpl(t.key)}
                    data-testid={`whatsapp-tpl-${t.key}`}
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors
                      ${tpl === t.key ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 text-slate-600 hover:border-emerald-300'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {!ctx.pdf_available && (
              <p className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                Boleto PDF indisponível — a mensagem seguirá com a linha digitável quando existir.
              </p>
            )}

            <div>
              <Label className="text-xs">Mensagem (editável)</Label>
              <Textarea rows={12} value={text} onChange={e => setText(e.target.value)}
                className="mt-1 font-mono text-xs" data-testid="whatsapp-message" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={openWhatsapp} disabled={loading || !ctx} data-testid="whatsapp-open"
            className="bg-emerald-600 hover:bg-emerald-700">
            <MessageCircle className="w-4 h-4 mr-2" /> Abrir WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsappSendDialog;
